import type { QueryClient } from '@tanstack/react-query';
import { AuditQueryKeys } from '../api/keys';
import {
  type AuditPatch,
  type CacheSnapshot,
  cancelAuditQueries,
  snapshotAndPatchAudit,
  rollbackAuditCache,
} from './patchAuditCache';

export type { AuditPatch, CacheSnapshot } from './patchAuditCache';

/**
 * Cancels in-flight queries for a specific audit's summary.
 * Used before optimistic updates to prevent race conditions.
 */
export async function cancelAuditSummaryQueries(
  queryClient: QueryClient,
  auditId: string,
): Promise<void> {
  await cancelAuditQueries(queryClient, AuditQueryKeys.summary, auditId);
}

/**
 * Snapshots current cache state and optimistically patches audit summary
 * queries for a given auditId. Returns snapshots for rollback.
 */
export function snapshotAndPatchAuditSummary(
  queryClient: QueryClient,
  auditId: string,
  patch: AuditPatch,
): CacheSnapshot[] {
  return snapshotAndPatchAudit(queryClient, AuditQueryKeys.summary, auditId, patch);
}

/**
 * Rolls back cached audit summary queries to their previous state.
 */
export function rollbackAuditSummaryCache(
  queryClient: QueryClient,
  snapshots: CacheSnapshot[],
): void {
  rollbackAuditCache(queryClient, snapshots);
}

/**
 * Patches all cached audit summary queries for a given auditId with
 * updated audit data from a mutation response. No-ops when the response
 * omitted the audit (SDK types it optional).
 *
 * Returns the number of cache entries patched.
 */
export function patchAuditSummaryCache(
  queryClient: QueryClient,
  auditId: string,
  updatedAudit: AuditPatch | undefined,
): number {
  if (!updatedAudit) return 0;
  const snapshots = snapshotAndPatchAuditSummary(queryClient, auditId, updatedAudit);
  return snapshots.length;
}
